import React, { useState, useCallback, memo } from 'react';
import { Box, Typography, Skeleton, useMediaQuery, useTheme } from '@mui/material';

const S3_LOGO_URL =
  'https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png';

/* ─── Fallback rendered when S3 image cannot be loaded ─────────────────────── */
const FallbackLogo = memo(({ size }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: size,
      height: size,
      borderRadius: '10px',
      background: 'linear-gradient(135deg, #6C3BFF 0%, #8B5CF6 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      boxShadow: '0 2px 8px rgba(108,59,255,0.28)',
    }}
  >
    <Typography
      sx={{
        color: '#FFFFFF',
        fontWeight: 900,
        fontSize: size * 0.52,
        lineHeight: 1,
        letterSpacing: '-0.02em',
        fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
      }}
    >
      T
    </Typography>
  </Box>
));
FallbackLogo.displayName = 'FallbackLogo';

/* ─── NextGenHeaderLogo ─────────────────────────────────────────────────────── */

/**
 * Production-grade header logo for the TSG Nexus module.
 *
 * Loads The Skill Genie logo from S3 with:
 *  - Lazy loading & async decoding
 *  - Zero layout shift (fixed bounding box during load)
 *  - Skeleton shimmer placeholder
 *  - Graceful TSG-branded fallback (never a broken image)
 *  - Full keyboard & ARIA accessibility
 *  - Responsive sizing (desktop / tablet / mobile)
 *  - Memoised to prevent unnecessary re-renders
 *
 * @param {function} onClick  - Called when the logo is clicked / activated
 */
const NextGenHeaderLogo = memo(({ onClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  /* Responsive logo height ─ 40 px desktop, 38 px tablet, 36 px mobile */
  const logoHeight = isMobile ? 36 : isTablet ? 38 : 42;

  const handleLoad = useCallback(() => setImgLoaded(true), []);

  const handleError = useCallback(() => {
    setImgError(true);
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[NextGenHeaderLogo] S3 logo failed to load:', S3_LOGO_URL);
    }
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.();
      }
    },
    [onClick],
  );

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label="The Skill Genie — Navigate to TSG Nexus Dashboard"
      onClick={onClick}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: 'pointer',
        flexShrink: 0,
        userSelect: 'none',
        outline: 'none',
        borderRadius: 2,
        p: 0.5,
        mx: -0.5,
        transition: 'opacity 0.18s ease',
        '&:hover': { opacity: 0.88 },
        '&:active': { opacity: 0.72 },
        '&:focus-visible': {
          outline: '2px solid #6C3BFF',
          outlineOffset: 3,
          borderRadius: 2,
        },
      }}
    >
      {/* ── Logo image — fixed height reserves space, preventing layout shift ── */}
      <Box
        sx={{
          position: 'relative',
          height: logoHeight,
          /* Reserve width only while loading so the skeleton doesn't collapse */
          width: imgLoaded || imgError ? 'auto' : logoHeight,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Skeleton shimmer shown until the image loads or errors */}
        {!imgLoaded && !imgError && (
          <Skeleton
            variant="rounded"
            width={logoHeight}
            height={logoHeight}
            animation="wave"
            sx={{
              borderRadius: '10px',
              bgcolor: 'rgba(108,59,255,0.08)',
              flexShrink: 0,
            }}
          />
        )}

        {/* Graceful TSG-branded fallback */}
        {imgError && <FallbackLogo size={logoHeight} />}

        {/* Primary S3-hosted logo */}
        {!imgError && (
          <Box
            component="img"
            src={S3_LOGO_URL}
            alt="The Skill Genie Logo"
            decoding="async"
            width="auto"
            height={logoHeight}
            onLoad={handleLoad}
            onError={handleError}
            sx={{
              height: logoHeight,
              width: 'auto',
              objectFit: 'contain',
              flexShrink: 0,
              /* Use opacity so the browser still loads the image even before it's visible */
              opacity: imgLoaded ? 1 : 0,
              position: imgLoaded ? 'relative' : 'absolute',
              imageRendering: '-webkit-optimize-contrast',
            }}
          />
        )}
      </Box>

      {/* ── Brand text ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0,
          lineHeight: 1,
        }}
      >
        {/* "The Skill Genie" — visible on all breakpoints */}
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            fontSize: isMobile ? '0.85rem' : '0.92rem',
            background: 'linear-gradient(135deg, #6C3BFF 0%, #8B5CF6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.02em',
            whiteSpace: 'nowrap',
            fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          }}
        >
          The Skill Genie
        </Typography>

        {/* "TSG Nexus" — hidden on mobile to keep the header compact */}
        <Typography
          component="span"
          sx={{
            fontWeight: 500,
            lineHeight: 1,
            fontSize: '0.65rem',
            color: '#94A3B8',
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
            fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          }}
        >
          TSG Nexus
        </Typography>
      </Box>
    </Box>
  );
});

NextGenHeaderLogo.displayName = 'NextGenHeaderLogo';

export { S3_LOGO_URL };
export default NextGenHeaderLogo;
